import { useLocation } from "react-router-dom";
import { AuthCard } from "../components/AuthCard";
import { LoginForm } from "../components/LoginForm";
import { SignedIn } from "../components/SignedIn";
import { readRedirectUri } from "../lib/redirect";
import { useSession } from "../lib/useSession";

/**
 * One page, three states: still asking, signed in, not signed in.
 *
 * The question is asked before the form is offered, and that ordering is the
 * point. A visitor sent here by a project whose access token had merely expired
 * gets their session renewed by /api/auth/session and never sees a password
 * field; asking for the password first and checking afterwards would prompt
 * everybody every fifteen minutes.
 *
 * redirectUri is read from the router's location rather than once at module
 * scope: this page shares a bundle with /admin now, and the admin guard sends
 * an unauthenticated visitor here with ?redirect_uri=/admin via client-side
 * navigation, which never re-runs module-level code.
 */
export function LoginPage() {
  const location = useLocation();
  const redirectUri = readRedirectUri(location.search);
  const session = useSession();

  if (session.isPending) {
    return (
      <AuthCard title="AI Labs" subtitle="Checking your session">
        {/* Deliberately quiet. This resolves in a few hundred milliseconds on a
            warm function, and a spinner that flashes reads as a fault. */}
        <div className="h-24 animate-pulse rounded-lg bg-adriatic-deep/5" aria-hidden="true" />
        <span className="sr-only">Checking your session</span>
      </AuthCard>
    );
  }

  // A network fault or a 5xx, which is the one case this page cannot work
  // around: it does not know whether there is a session, so offering a form
  // would be a guess. The refresh is the honest control.
  if (session.isError) {
    return (
      <AuthCard title="Sign-in is unavailable" subtitle="The service did not respond">
        <div className="space-y-4">
          <p role="alert" className="text-sm text-adriatic-mid">
            This is a fault on our side rather than a problem with your account. If it persists,
            tell the AI Labs administrator.
          </p>
          <button
            type="button"
            onClick={() => session.refetch()}
            className="w-full rounded-full bg-coral px-4 py-2.5 font-semibold text-white transition-colors hover:bg-coral-light hover:text-adriatic-deep focus-visible:ring-2 focus-visible:ring-acqua/50 focus-visible:outline-none"
          >
            Try again
          </button>
        </div>
      </AuthCard>
    );
  }

  if (session.data.authenticated) {
    return (
      <AuthCard title="You are signed in" subtitle="Continue to AI Labs">
        <SignedIn user={session.data.user} redirectUri={redirectUri} />
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Sign in to AI Labs" subtitle="Use your Acquire Intelligence account">
      <LoginForm redirectUri={redirectUri} />
    </AuthCard>
  );
}
